import { getArrayValue } from "../array";
import { glsl_functions, glsl_utilities } from "../glsl";
import { GLSL_ARG, GLSL_TYPE_LOOKUP, toDataType } from "../glsl/typeMapping";
import DEFINES from "../glsl/utilities/defines";
import { Texture } from "../texture";
import { trimSource } from "../utils";
import { GL_FLOAT } from "./buffer";
import { GL_FRAGMENT_SHADER, GL_VERTEX_SHADER, ShaderVaryingsMode, } from "./shader";
export const Generate = {
    version: "300 es",
    function(type, name, inputs, source = "", onlyDeclaration = false) {
        const sanitizedInputs = Generate.sanitizeInput(inputs);
        const hasSampler2D = sanitizedInputs.some(({ type }) => type === "sampler2D");
        const variants = hasSampler2D ? ["sampler2D", "isampler2D", "usampler2D"] : [null];
        return variants
            .map(variant => {
            const fnArgs = sanitizedInputs.map(({ name, type }) => {
                const resolvedType = type === "sampler2D" && variant ? variant : type;
                return `${resolvedType} ${name}`;
            });
            const declaration = `${GLSL_TYPE_LOOKUP[type].returnType} ${name}(${[
                ...GLSL_TYPE_LOOKUP[type].args,
                ...fnArgs,
            ].join(", ")})`;
            return onlyDeclaration ? declaration : `${declaration}{\n\t${trimSource(source)}\n}`;
        })
            .join("\n");
    },
    fragment(precision, generator, shaderUniforms = [], shaderDefines = {}, shaderVaryings = {}, options = {}) {
        generator = { ...generator };
        if (!generator.code)
            generator.code = [];
        generator.code = generator.code.filter(c => c.name !== "generate");
        let entryName = "generate";
        const entry = generator.code.find(c => c.type === "src");
        if (entry) {
            entryName = entry.name;
            entry.source = Generate.sanitizeFunctionCode(generator.value(GLSL_ARG.st));
        }
        else {
            generator.code.push({
                type: "src",
                inputs: [],
                name: "generate",
                source: `return ${generator && generator.value ? generator.value(GLSL_ARG.st) : `vec4(${GLSL_ARG.st}, 1.)`};`,
            });
        }
        // determine output type and scaling
        const outputType = options.outputType ?? "float";
        const outputScale = typeof options.outputScale === "number" ? options.outputScale : 1;
        const scaleLiteral = Generate.numberFormatter(outputScale);
        const fragmentDefines = outputType === "float" ? shaderDefines : { ...shaderDefines, TEX_INT_SCALE: scaleLiteral };
        const fragType = outputType === "uint" ? "uvec4" : outputType === "int" ? "ivec4" : "vec4";
        let fragAssignment = "fragColor = _color;";
        if (outputType === "uint") {
            fragAssignment = `fragColor = uvec4(clamp(_color, 0.0, 1.0) * ${scaleLiteral} + 0.5);`;
        }
        else if (outputType === "int") {
            fragAssignment = `
				vec4 _signed = clamp(_color, 0.0, 1.0) * 2.0 - 1.0;
				fragColor = ivec4(_signed * ${scaleLiteral} + sign(_signed) * 0.5);
			`;
        }
        return Generate.glsl(GL_FRAGMENT_SHADER, precision, generator, shaderUniforms, fragmentDefines, {
            ...shaderVaryings,
            fragColor: {
                type: fragType,
                mode: ShaderVaryingsMode.OUT,
                shader: GL_FRAGMENT_SHADER,
            },
        }, `		
				vec3 ${GLSL_ARG.st} = vec3(gl_FragCoord.xy, .0);
				#ifdef HAS_POSITION
				${GLSL_ARG.st} = vPosition;
				#endif
				#ifdef HAS_TEXCOORD
				${GLSL_ARG.st} = vec3(vTexCoord, 0.0);
				#endif
				
				vec4 _color = vec4(1.0);
				#ifdef HAS_VERTEXCOLOR
				_color = vVertexcolor;
				#endif
				_color = ${entryName}(${GLSL_ARG.st});
				${fragAssignment}
			`);
    },
    vertex(precision, generator, shaderUniforms = [], shaderDefines = {}, shaderVaryings = {}) {
        generator = { ...generator };
        if (!generator.code)
            generator.code = [];
        generator.code = generator.code.filter(c => c.name !== "generate");
        let entryName = "generate";
        const entry = generator.code.find(c => c.type === "src");
        if (entry) {
            entryName = entry.name;
            entry.source = Generate.sanitizeFunctionCode(generator.value(GLSL_ARG.st));
        }
        else {
            generator.code.push({
                type: "src",
                inputs: [],
                name: "generate",
                source: `return ${generator && generator.value ? generator.value(GLSL_ARG.st) : `vec4(${GLSL_ARG.st}, 1.)`};`,
            });
        }
        return Generate.glsl(GL_VERTEX_SHADER, precision, generator, shaderUniforms, shaderDefines, shaderVaryings, `	
				mat4 modelViewMatrix = mat4(
					1.0f, 0.0f, 0.0f, 0.0f,
					0.0f, 1.0f, 0.0f, 0.0f,
					0.0f, 0.0f, 1.0f, 0.0f,
					0.0f, 0.0f, 0.0f, 1.0f
				);

				#ifdef HAS_CAMERA
				modelViewMatrix *= viewMatrix;
				#endif

				#ifdef HAS_MODEL_MATRIX
				modelViewMatrix *= modelMatrix;
				#endif

				#ifdef HAS_NORMAL
				vNormal = transpose(inverse(mat3(modelViewMatrix))) * ${entryName}(normal).xyz;
				#endif
				#ifdef HAS_TANGENT
				vTangent = ${entryName}(tangent.xyz); 
				#endif

				vec3 initial_position = vec3(0.0);
				#ifdef HAS_POSITION
				initial_position = position;
				#endif

				vec4 generatedPosition = ${entryName}(initial_position);
				
				#ifdef HAS_POSITION
				vPosition = generatedPosition.xyz * 0.5 + 0.5;
				#endif

				#ifdef HAS_CAMERA
				gl_Position = projectionMatrix * modelViewMatrix * generatedPosition;
				#else
				gl_Position = modelViewMatrix * generatedPosition;
				#endif

				#ifdef HAS_TEXCOORD
				vTexCoord = texCoord;
				#endif
	
				#ifdef HAS_VERTEXCOLOR
				vVertexcolor = vertexColor;
				#endif

				#ifdef HAS_INSTANCE
				vInstance = instance;
				vInstanceOffset = instanceOffset;
				#endif

				#ifdef HAS_POINTSIZE
					gl_PointSize = pointSize;
				#endif
			`);
    },
    generator(generator, shader, shaderUniforms = [], shaderDefines = {}, shaderVaryings = {}) {
        const defines = [...(generator.defines || [])]
            .map(define => (DEFINES[define] ? `#define ${define} ${DEFINES[define]}` : define))
            .concat(Object.keys(shaderDefines).map(key => `#define ${key} ${typeof shaderDefines[key] === "boolean" ? (shaderDefines[key] ? 1 : 0) : shaderDefines[key]}`));
        const structs = [];
        const uniformsDefinitions = [...(generator.uniforms || []), ...shaderUniforms]
            .filter((uniform, index, self) => self.findIndex(u => u.name === uniform.name) === index)
            .map(uniform => `uniform ${uniform.type} ${uniform.name}`)
            .sort((a, b) => a.localeCompare(b));
        const varyingsDefitions = [
            ...(generator.varyings || []),
            ...(shader ? Generate.varyngs(shader, shaderVaryings) : []),
        ].sort((a, b) => a.localeCompare(b));
        const utilitiesList = ["tex", ...(generator.utilities || [])].filter(utility => utility && (typeof utility !== "string" || utility.length > 0));
        const utilities = utilitiesList
            .filter((utility, index) => utilitiesList.indexOf(utility) === index)
            .map(utility => glsl_utilities[utility] ?? utility);
        const fns = [...(generator.functions || [])].map(fnNameOrFn => {
            const f = (typeof fnNameOrFn === "string"
                ? glsl_functions.find(f => f.name === fnNameOrFn && f.type !== "code")
                : fnNameOrFn);
            return Generate.function(f.type, f.name, f.inputs, f.glsl);
        });
        /*const fnsDeclarations = [...(generator.functions || [])].map(fnNameOrFn => {
            const f = (
                typeof fnNameOrFn === "string"
                    ? glsl_functions.find(f => f.name === fnNameOrFn && f.type !== "code")
                    : fnNameOrFn
            ) as GLSLFunctionSource

            return Generate.function(f.type, f.name, f.inputs, null, true)
        })*/
        const codes = [...(generator.code || [])].map(code => Generate.function(code.type, code.name, code.inputs, code.source));
        const functions = [...utilities, ...fns, ...codes];
        const join = (arr, s = "") => arr.join(s + "\n") + (arr.length > 0 ? s : "") + "\n";
        return [
            join(defines),
            join(uniformsDefinitions, ";"),
            join(structs, ";"),
            join(varyingsDefitions, ";"),
            //join(fnsDeclarations, ";"),
            join(functions),
        ]
            .join("\n")
            .replaceAll(/\n\n\n/g, "\n\n");
    },
    glsl(shader, precision, generator, shaderUniforms = [], shaderDefines = {}, shaderVaryings = {}, body) {
        const source = Generate.generator(generator, shader, shaderUniforms, shaderDefines, shaderVaryings);
        return [
            `#version ${Generate.version}`,
            `precision ${precision} float;`,
            `precision ${precision} int;`,
            `precision ${precision} sampler2D;`,
            `precision ${precision} isampler2D;`,
            `precision ${precision} usampler2D;`,
            `precision ${precision} samplerCube;`,
            `precision ${precision} sampler2DArray;`,
            source,
            "void main() {",
            body.trim().replaceAll(/\t/g, "").replaceAll(/\n/g, "\n\t"),
            "}",
        ]
            .join("\n")
            .replaceAll(/\n\n\n/g, "\n\n");
    },
    uniforms(synth, uniforms = {}) {
        if (!Array.isArray(uniforms))
            return uniforms; // already ShaderUniforms
        const result = {};
        for (let i = 0, len = uniforms.length; i < len; i++) {
            const uniform = uniforms[i];
            //if (typeof uniform.value !== "undefined") {
            let value = typeof uniform.value === "function" ? uniform.value(synth) : uniform.value;
            if (value instanceof Texture) {
                // se decommento non funziona la sequence, probabilemente bisogna attivare la texture
                // value.next()
                value = value.texture();
            }
            else {
                // let realType = Generate.toUniformType(value)
                // TODO: remove type 'synth' from Uniforms['type'], to test
                //if (realType !== -1 && realType !== uniform.type && Array.isArray(value)) {
                if (uniform.type === "float" && Array.isArray(value)) {
                    value = getArrayValue(value, {
                        time: synth.time,
                        bpm: synth.bpm,
                    });
                }
                //}
            }
            result[uniform.name] = [uniform.type, value];
        }
        return result;
    },
    varyngs(shaderType, varyings) {
        const result = [];
        Object.keys(varyings).forEach(name => {
            const { type, shader, mode } = varyings[name];
            if (typeof shader !== "undefined" && shaderType !== shader)
                return;
            const isIntegerVarying = /^(u?int|[iu]vec[234])$/.test(type);
            const isFragmentOutput = shaderType === GL_FRAGMENT_SHADER && mode === ShaderVaryingsMode.OUT;
            const prefix = isIntegerVarying && !isFragmentOutput && !(shaderType === GL_VERTEX_SHADER && mode === ShaderVaryingsMode.IN)
                ? "flat "
                : "";
            switch (true) {
                case mode === ShaderVaryingsMode.IN ||
                    (mode === ShaderVaryingsMode.SHARED && shaderType === GL_FRAGMENT_SHADER):
                    result.push(`${prefix}in ${type} ${name}`);
                    break;
                case mode === ShaderVaryingsMode.OUT || (mode === ShaderVaryingsMode.SHARED && shaderType === GL_VERTEX_SHADER):
                    result.push(`${prefix}out ${type} ${name}`);
                    break;
            }
        });
        return result;
    },
    merge(source, toAdd) {
        if (!source.uniforms)
            source.uniforms = [];
        if (!source.functions)
            source.functions = [];
        if (!source.utilities)
            source.utilities = [];
        if (!source.varyings)
            source.varyings = [];
        if (!source.code)
            source.code = [];
        if (!source.defines)
            source.defines = [];
        if (!source.dependencies)
            source.dependencies = [];
        (toAdd.uniforms || []).forEach(uniform => {
            const existing = source.uniforms.find(({ name }) => name === uniform.name);
            if (!existing)
                source.uniforms.push(uniform);
        });
        (toAdd.functions || []).forEach(fn => {
            if (!source.functions.includes(fn))
                source.functions.push(fn);
        });
        (toAdd.utilities || []).forEach(fn => {
            if (!source.utilities.includes(fn))
                source.utilities.push(fn);
        });
        (toAdd.code || []).forEach(fn => {
            if (!source.code.find(c => c.name === fn.name))
                source.code.push(fn);
        });
        (toAdd.defines || []).forEach(define => {
            if (!source.defines.includes(define))
                source.defines.push(define);
        });
        (toAdd.varyings || []).forEach(varyng => {
            if (!source.varyings.includes(varyng))
                source.varyings.push(varyng);
        });
        (toAdd.dependencies || []).forEach(dep => {
            if (!source.dependencies.includes(dep))
                source.dependencies.push(dep);
        });
    },
    toUniformType(value) {
        if (typeof value === "function")
            value = value();
        if (value instanceof Texture)
            return value.samplerType();
        if (Number.isNaN(value) ||
            value instanceof WebGLTexture ||
            value instanceof WebGLFramebuffer ||
            (typeof value === "number" && isNaN(value)))
            return "sampler2D";
        if (typeof value === "number")
            return "float";
        if (Array.isArray(value) || value instanceof Float32Array) {
            if (typeof value[0] === "number") {
                return toDataType(value.length, GL_FLOAT); // TODO: check this, GL_FLOAT is not correct
            }
        }
        //throw new Error(`Uniform type not supported for value "${value}(${typeof value})"`)
        console.warn(`Uniform type not supported for value "${value}(${typeof value})"`, value);
        return -1;
    },
    sanitizeInput(input) {
        if (!input)
            return [];
        return !Array.isArray(input)
            ? Object.entries(input).map(([name, value]) => {
                const type = Generate.toUniformType(value);
                if (type === -1) {
                    console.warn("Generate.sanitizeInput", name, value, type);
                    throw new Error(`Uniform '${name}' is not supported`);
                }
                return {
                    name,
                    default: value,
                    type: type,
                };
            })
            : input;
    },
    sanitizeUniforms(uniforms) {
        const result = [];
        if (Array.isArray(uniforms)) {
            return uniforms
                .map(uniform => ({
                name: uniform.name,
                value: uniform.value || uniform.default,
                type: uniform.type,
            }))
                .filter(({ value }) => value !== null);
        }
        if (typeof uniforms === "object") {
            Object.entries(uniforms).forEach(([name, value]) => {
                if (value !== null) {
                    const type = Generate.toUniformType(value);
                    if (type !== -1) {
                        result.push({
                            name,
                            value: value,
                            type: type,
                        });
                    }
                }
            });
        }
        return result.filter(({ value }) => value !== null);
    },
    sanitizeShaderString(str, precition) {
        str = trimSource(str);
        // chck str has main
        if (!str.includes("main"))
            str = `void main(){\n${Generate.sanitizeFunctionCode(str, "")}\n}`;
        // check str init with #version
        if (!str.trim().startsWith("#version"))
            str = `#version ${Generate.version}\n${str}`;
        // check str has precision after #version
        const precisionTypes = [
            "float",
            "int",
            "sampler2D",
            "isampler2D",
            "usampler2D",
            "samplerCube",
            "sampler2DArray",
        ];
        const missing = precisionTypes.filter(type => !new RegExp(`precision\\s+\\w+\\s+${type}\\s*;`).test(str));
        if (missing.length) {
            const lines = missing.map(type => `precision ${precition} ${type};`).join("\n");
            str = str.replace(`#version ${Generate.version}`, `#version ${Generate.version}\n${lines}`);
        }
        return str;
    },
    sanitizeFunctionCode(code, finalAssign = "return") {
        if (code.length < 1)
            return code;
        // trim emspty lines
        code = code.trim();
        // trim empty \n
        code = code.replaceAll("\n\n", "\n");
        // get final line
        const lines = code.split("\n");
        const ll = lines.length - 1;
        let lastLine = lines[ll].trim();
        // remove last line if empty
        if (!lastLine) {
            lines.pop();
            lastLine = lines[ll - 1].trim();
        }
        // check if last line is a return
        if (!code.includes(finalAssign) && lastLine.substring(0, 6) !== finalAssign) {
            // add return
            lines[ll] = finalAssign + " " + lastLine;
        }
        code = lines.join("\n");
        // add ; if not present
        if (code.substring(code.length - 1) != ";")
            code += ";";
        return code;
    },
    numberFormatter(value) {
        return Number.isInteger(value) ? value + "." : value.toString();
    },
};
